// SGNL-134 — Admin AI Audit sub-panel.
//
// Renders the side-by-side rep vs LLM AI-classification audit, sourced
// from /api/admin/ai-audit (BOOTSTRAP_ADMINS-gated server-side). Hosted
// as a sub-tab of the Admin tab — keeping it off the main nav so it
// stays an admin tool, not a public dashboard.
//
// Out of scope (filed separately):
//   SGNL-128 — clickable rationale → source-row citations
//   SGNL-6   — Apply-LLM-to-SF write-back

import { fetchAIAudit } from "../api";
import { esc, fmtMoney } from "../lib/format";
import type {
  AIAuditDisagreement,
  AIAuditRecentVerdict,
  AIAuditResponse,
  AIAuditSide,
} from "../types";

const REP_BUCKET_LABEL: Record<string, string> = {
  selling: "Selling",
  using: "Using",
  non: "Non-AI",
};

const LLM_BUCKET_LABEL: Record<string, string> = {
  "selling-llm": "Selling-LLM",
  "using-llm": "Using-LLM",
  "non-llm": "Non-LLM",
  unclassified: "Unclassified",
};

function sideTable(rep: AIAuditSide, llm: AIAuditSide): string {
  // Two-row grouped header — Rep classification / LLM classification,
  // each spanning Selling / Using / Non-AI. Matches the AC layout.
  return `
    <table class="data-table ai-audit-grid" data-testid="ai-audit-grid">
      <thead>
        <tr class="ai-audit-group-row">
          <th></th>
          <th colspan="3" class="ai-audit-group-header rep">Rep classification</th>
          <th colspan="3" class="ai-audit-group-header llm">LLM classification</th>
        </tr>
        <tr>
          <th></th>
          <th class="num">Selling</th>
          <th class="num">Using</th>
          <th class="num">Non-AI</th>
          <th class="num">Selling-LLM</th>
          <th class="num">Using-LLM</th>
          <th class="num">Non-LLM</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <th class="row-label">Count</th>
          <td class="num">${rep.selling.count.toLocaleString()}</td>
          <td class="num">${rep.using.count.toLocaleString()}</td>
          <td class="num">${rep.non_ai.count.toLocaleString()}</td>
          <td class="num">${llm.selling.count.toLocaleString()}</td>
          <td class="num">${llm.using.count.toLocaleString()}</td>
          <td class="num">${llm.non_ai.count.toLocaleString()}</td>
        </tr>
        <tr>
          <th class="row-label">Total $ (CY)</th>
          <td class="num">${fmtMoney(rep.selling.total_cy_amount)}</td>
          <td class="num">${fmtMoney(rep.using.total_cy_amount)}</td>
          <td class="num">${fmtMoney(rep.non_ai.total_cy_amount)}</td>
          <td class="num">${fmtMoney(llm.selling.total_cy_amount)}</td>
          <td class="num">${fmtMoney(llm.using.total_cy_amount)}</td>
          <td class="num">${fmtMoney(llm.non_ai.total_cy_amount)}</td>
        </tr>
      </tbody>
    </table>`;
}

function disagreementSection(disagreements: AIAuditDisagreement[]): string {
  if (disagreements.length === 0) return "";
  return `
    <h3 class="ai-audit-subhead">Disagreements (drill-through to Deal Explorer)</h3>
    <table class="data-table ai-audit-disagree-grid" data-testid="ai-audit-disagree-grid">
      <thead>
        <tr>
          <th>Mismatch</th>
          <th class="num">Opps</th>
          <th class="num">Total $ (CY)</th>
        </tr>
      </thead>
      <tbody>
        ${disagreements
          .map(
            (d) => `
          <tr data-testid="ai-audit-disagree-row" data-label="${esc(d.label)}">
            <td>${esc(d.label)}</td>
            <td class="num">${d.count.toLocaleString()}</td>
            <td class="num">${fmtMoney(d.total_cy_amount)}</td>
          </tr>`,
          )
          .join("")}
      </tbody>
    </table>`;
}

function recentSection(rows: AIAuditRecentVerdict[]): string {
  if (rows.length === 0) {
    return `<p class="muted">No classified opps yet — wait for the next classifier cron run.</p>`;
  }
  return `
    <h3 class="ai-audit-subhead">Recent LLM verdicts (newest first, ${rows.length})</h3>
    <table class="data-table ai-audit-recent-grid" data-testid="ai-audit-recent-grid">
      <thead>
        <tr>
          <th>Opportunity</th>
          <th>Account</th>
          <th>Owner</th>
          <th>Stage</th>
          <th>Rep</th>
          <th>LLM</th>
          <th class="num">Sol&nbsp;conf</th>
          <th class="num">Del&nbsp;conf</th>
        </tr>
      </thead>
      <tbody>
        ${rows
          .map(
            (r) => `
          <tr data-testid="ai-audit-recent-row" data-opp-id="${esc(r.opp_id)}">
            <td>${esc(r.opp_name)}</td>
            <td>${esc(r.account_name ?? "—")}</td>
            <td>${esc(r.owner_name ?? "—")}</td>
            <td>${esc(r.stage ?? "—")}</td>
            <td>${esc(r.rep_bucket ? REP_BUCKET_LABEL[r.rep_bucket] : "—")}</td>
            <td>${esc(LLM_BUCKET_LABEL[r.llm_bucket] ?? r.llm_bucket)}</td>
            <td class="num">${r.llm_solution_confidence != null ? r.llm_solution_confidence.toFixed(2) : "—"}</td>
            <td class="num">${r.llm_delivery_confidence != null ? r.llm_delivery_confidence.toFixed(2) : "—"}</td>
          </tr>`,
          )
          .join("")}
      </tbody>
    </table>`;
}

export async function renderAIAuditPanel(host: HTMLElement): Promise<void> {
  host.innerHTML = `<div class="loading">Loading AI audit…</div>`;
  let data: AIAuditResponse;
  try {
    data = await fetchAIAudit();
  } catch (e) {
    const msg = (e as Error).message;
    // Surface a friendlier hint on the most common admin-gate failure
    // (403 from current_admin) so the operator knows what to do.
    const hint = msg.includes("403")
      ? " — your account isn't in BOOTSTRAP_ADMINS. Ask an admin to add your email."
      : "";
    host.innerHTML = `<div class="error" data-testid="ai-audit-error">Failed to load AI audit: ${esc(msg)}${hint}</div>`;
    return;
  }

  // Surface the unclassified count separately so the LLM-total gap
  // vs the rep-total is explained — admins were otherwise left to
  // guess why "rep selling 12, LLM selling 9" doesn't reconcile when
  // it's actually 3 not-yet-classified opps. SGNL-134 code-reviewer
  // CR-001 on PR #68.
  const unclassified = data.llm.unclassified_count ?? 0;
  const unclassifiedLine =
    unclassified > 0
      ? `<p class="muted" data-testid="ai-audit-unclassified">
           <b>${unclassified.toLocaleString()}</b> opps not yet classified
           by the LLM (excluded from LLM bucket totals; will appear after
           the next classifier cron run).
         </p>`
      : "";

  host.innerHTML = `
    <div class="ai-audit-panel" data-testid="ai-audit-panel">
      <p class="muted">
        Side-by-side rep tags vs LLM verdicts. The public AI Breakdown
        dashboard shows rep-only headlines so execs see only the
        numbers they've been citing; this view is the reconciling one
        for admins. Latest classifier prompt:
        <code>${esc(data.latest_classifier_prompt_version ?? "(no classified opps yet)")}</code>.
      </p>
      ${unclassifiedLine}
      ${sideTable(data.rep, data.llm)}
      ${disagreementSection(data.disagreements)}
      ${recentSection(data.recent_verdicts)}
    </div>`;
}
